import type { Platform } from '../../types/Platforms';
import { getAccessToken } from '../authentication/supabaseService';
export type StepKey = 'pos' | 'supplier_invoices' | 'invoice_forwarding' | 'recipes_cost_prices' | 'cost_price_review';
export type FileStepKey = 'supplier_invoices' | 'recipes_cost_prices';
export type CloudStatus = 'not_started' | 'in_progress' | 'needs_input' | 'done' | 'not_applicable';
export const STEP_KEYS: StepKey[] = ['pos', 'supplier_invoices', 'invoice_forwarding', 'recipes_cost_prices', 'cost_price_review'];
export const CLOUD_STATUSES: CloudStatus[] = ['not_started', 'in_progress', 'needs_input', 'done', 'not_applicable'];
export interface FileReceipt {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  submitted: boolean;
  supplierPlatform?: string;
  notInvoice?: boolean;
}
export interface PosInput {
  provider: string;
  otherProvider?: string;
  username: string;
  password?: string;
  portalUrl?: string;
  venueName?: string;
}
export interface SetupStep {
  restaurantId: string;
  stepKey: StepKey;
  customerDone: boolean;
  customerStarted?: boolean;
  cloudStatus: CloudStatus;
  revision: number;
  updatedAt: string;
  files: FileReceipt[];
  fileCount?: number;
  forwardingAlias?: string;
  pos?: Omit<PosInput, 'password'> & {
    submittedAt?: string;
    hasPassword: boolean;
  };
  reviewRunId: string | null;
  reviewCompletedAt: string | null;
  reviewAvailable?: boolean;
}
export interface SetupRestaurant {
  id: string;
  name: string;
  steps: SetupStep[];
  availableReviewRuns?: {
    id: string;
    name: string;
  }[];
}
export interface SetupOverview {
  restaurants: SetupRestaurant[];
  demoAvailable: boolean;
}
export interface SetupAccount {
  id: string;
  name: string | null;
  email: string;
  setupEnabled: boolean;
  restaurants: SetupRestaurant[];
  updatedAt?: string;
  demoRestaurantIds?: string[];
}
export interface DemoRestaurant {
  id: string;
  name: string;
  ownerId: string;
  ownerEmail: string;
}
export class SetupApiError extends Error {
  constructor(public code: string, public status: number) {
    super(code);
  }
}
export async function setupRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const response = await fetch(path, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body instanceof FormData ? {} : {
        'Content-Type': 'application/json'
      }),
      ...options.headers
    }
  });
  const data = await response.json();
  if (!response.ok) throw new SetupApiError(data.error || 'request_failed', response.status);
  return data;
}
const base = '/api/account-setup';
const admin = '/api/admin/account-setup';
const restaurantPath = (restaurantId: string, employee = false) => `${employee ? admin : base}/restaurants/${encodeURIComponent(restaurantId)}`;
const stepPath = (restaurantId: string, key: StepKey, employee = false) => `${restaurantPath(restaurantId, employee)}/steps/${key}`;
export const getSetupOverview = () => setupRequest<SetupOverview>(base);
export const getSetupRestaurant = (id: string) => setupRequest<SetupRestaurant>(`${restaurantPath(id)}`);
export const saveForwardingAlias = (step: SetupStep, alias: string) => setupRequest<SetupStep>(`${restaurantPath(step.restaurantId, true)}/forwarding`, {
  method: 'PUT', body: JSON.stringify({ revision: step.revision, alias }),
});
export const submitForwarding = (step: SetupStep) => setupRequest<SetupStep>(`${restaurantPath(step.restaurantId)}/forwarding/submit`, {
  method: 'POST', body: JSON.stringify({ revision: step.revision }),
});
export const savePos = (restaurantId: string, input: PosInput) => setupRequest<SetupStep>(`${restaurantPath(restaurantId)}/pos`, {
  method: 'PUT',
  body: JSON.stringify(input)
});
export function uploadSetupFile(restaurantId: string, key: FileStepKey, fileId: string, file: File, signal?: AbortSignal) {
  const body = new FormData();
  body.append('file', file);
  body.append('fileId', fileId);
  return setupRequest<FileReceipt>(`${stepPath(restaurantId, key)}/files`, {
    method: 'POST',
    body,
    signal
  });
}
export const submitSetupFiles = (restaurantId: string, key: FileStepKey, fileIds: string[]) => setupRequest<SetupStep>(`${stepPath(restaurantId, key)}/submit`, {
  method: 'POST',
  body: JSON.stringify({
    fileIds
  })
});
export const getSetupAccounts = (filter: string) => setupRequest<{
  accounts: SetupAccount[];
}>(`${admin}?filter=${encodeURIComponent(filter)}`);
export const getSetupAccount = (id: string) => setupRequest<SetupAccount>(`${admin}/accounts/${encodeURIComponent(id)}`);
export const getDemoCandidates = () => setupRequest<{
  restaurants: DemoRestaurant[];
}>(`${admin}/demo-restaurants`);
export const saveDemoLinks = (id: string, restaurantIds: string[]) => setupRequest<{
  restaurantIds: string[];
}>(`${admin}/accounts/${encodeURIComponent(id)}/demo-restaurants`, {
  method: 'PUT',
  body: JSON.stringify({
    restaurantIds
  })
});
export const updateSetupStep = (step: SetupStep, cloudStatus: CloudStatus, customerDone?: boolean) => setupRequest<SetupStep>(stepPath(step.restaurantId, step.stepKey, true), {
  method: 'PUT',
  body: JSON.stringify({
    revision: step.revision,
    cloudStatus,
    customerDone
  })
});
export const assignSetupReview = (step: SetupStep, reviewRunId: string | null) => setupRequest<SetupStep>(`${restaurantPath(step.restaurantId, true)}/review`, {
  method: 'PUT',
  body: JSON.stringify({
    revision: step.revision,
    reviewRunId
  })
});
export interface SetupCostPriceImportResult {
  runId: string | null; itemsProcessed: number; ingredientsAdded: number; menuAssignmentFailed: boolean;
}
export function importSetupCostPrices(restaurantId: string, file: File, options: {
  overallType: 'all' | 'food' | 'drinks'; vatRate: '9' | '21'; variant: 'standard' | 'new_menu'; newMenuName?: string;
}) {
  const body = new FormData();
  body.append('file', file);
  body.append('overallType', options.overallType);
  body.append('vatRate', options.vatRate);
  body.append('variant', options.variant);
  if (options.variant === 'new_menu' && options.newMenuName) body.append('newMenuName', options.newMenuName.trim());
  return setupRequest<SetupCostPriceImportResult>(`${restaurantPath(restaurantId, true)}/review/import`, { method: 'POST', body });
}
export const revealSetupPassword = (id: string) => setupRequest<{
  password: string;
}>(`${restaurantPath(id, true)}/pos/password`);
export async function downloadSetupFile(restaurantId: string, key: StepKey, fileId: string, employee = false) {
  const {
    url
  } = await setupRequest<{
    url: string;
  }>(`${stepPath(restaurantId, key, employee)}/files/${encodeURIComponent(fileId)}/download`);
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  a.click();
}
export async function downloadSetupArchive(restaurantId: string, key: StepKey, scope?: 'unknown') {
  const {
    url
  } = await setupRequest<{
    url: string;
  }>(`${stepPath(restaurantId, key, true)}/files/download-all?prepare=1${scope ? `&scope=${scope}` : ''}`);
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  a.click();
}
export const stepComplete = (step: SetupStep) => step.cloudStatus === 'not_applicable' || (step.customerDone && step.cloudStatus === 'done');
/** The customer can act now; an unpublished cost-price review is still team work. */
export const awaitingCustomer = (step: SetupStep) => !step.customerDone && step.cloudStatus !== 'not_applicable'
  && (step.stepKey !== 'cost_price_review' || !!step.reviewAvailable);
export function setupProgress(steps: SetupStep[]): number | null {
  const applicable = steps.filter(step => step.cloudStatus !== 'not_applicable');
  return applicable.length ? Math.round(applicable.reduce((sum, step) => sum + Number(step.customerDone) + Number(step.cloudStatus === 'done'), 0) / (applicable.length * 2) * 100) : null;
}

/** Authenticated download: the server uses the saved alias for this restaurant and revision. */
export async function downloadForwardingGuide(step: SetupStep, provider: string, language: 'en' | 'nl') {
  const token = await getAccessToken();
  const query = new URLSearchParams({ provider, language, revision: String(step.revision) });
  const response = await fetch(`${restaurantPath(step.restaurantId)}/forwarding/guide?${query}`, {
    headers: { Authorization: `Bearer ${token}` }, cache: 'no-store',
  });
  if (!response.ok) {
    const data = await response.json();
    throw new SetupApiError(data.error || 'request_failed', response.status);
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url; link.download = `Culinary-Cloud-${provider}-${language}.pdf`;
  document.body.appendChild(link); link.click(); link.remove();
  // Give the browser time to start consuming the object URL.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const completeSetupCollection = (step: SetupStep) => setupRequest<SetupStep>(`${stepPath(step.restaurantId, step.stepKey)}/complete`, { method: 'POST', body: JSON.stringify({ revision: step.revision }) });
export const verifySetupStep = (step: SetupStep) => setupRequest<SetupStep>(`${stepPath(step.restaurantId, step.stepKey, true)}/verify`, { method: 'POST', body: JSON.stringify({ revision: step.revision }) });
export type CustomerSetupStatus = 'not_started' | 'in_progress' | 'pending' | 'completed' | 'not_applicable';
export function customerSetupStatus(step: SetupStep): CustomerSetupStatus {
  if (step.cloudStatus === 'not_applicable') return 'not_applicable';
  if (step.customerDone) return step.cloudStatus === 'done' ? 'completed' : 'pending';
  return step.customerStarted || step.files.length > 0 || !!step.pos || !!step.reviewRunId ? 'in_progress' : 'not_started';
}
export const SETUP_POS_PROVIDERS = ['booq', 'trivecgroup', 'tebi', 'gastrodesk', 'until', 'cashdesk', 'scanfie', 'lightspeed', 'mpluskassa', 'twelve', 'oracle_micros_simphony', 'vectron'] as const;
export interface SetupStaffUser { id: string; email: string; restaurant_ids: string[]; restaurant_role?: string | null; access_request?: string | null; }
export const getSetupStaff = () => setupRequest<{ staff: SetupStaffUser[]; restaurants: { id: string; name: string }[] }>(`${base}/staff`);

export async function createSetupStaff(email: string, password: string, restaurantRole: string, restaurantIds: string[], _access?: unknown, locale?: string): Promise<void> {
  await setupRequest(`${base}/staff/create`, { method: 'POST', body: JSON.stringify({ email, password, restaurantRole, restaurantIds, locale }) });
}

export interface SetupRecognitionResult { revision: number; matched: number; unresolved: number; failed: number; }
export type SetupSupplierFileTarget = Exclude<Platform, Platform.ANY_INVOICE> | 'other' | 'not_invoice';
export const recognizeSetupSupplierFiles = (restaurantId: string, revision: number, fileIds: string[]) => setupRequest<SetupRecognitionResult>(`${restaurantPath(restaurantId, true)}/supplier-files/recognize`, {
  method: 'POST', body: JSON.stringify({ revision, fileIds }),
});
export const assignSetupSupplierFiles = (restaurantId: string, revision: number, fileIds: string[], target: SetupSupplierFileTarget) => setupRequest<{ revision: number; changed: number }>(`${restaurantPath(restaurantId, true)}/supplier-files/assign`, {
  method: 'POST', body: JSON.stringify({ revision, fileIds, target }),
});
